"use client";

import { ArrowRight, CheckCircle2, Flame } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const treatments = [{
  title: "Door and window perimeter treatment",
  desc: "Targeted barrier treatments applied around door and window frames to deter crawling and flying insects."
}, {
  title: "Garage door and threshold treatment",
  desc: "Focused applications along ground transitions, garage thresholds, and exterior access seams."
}, {
  title: "Utility and pipe penetration treatment",
  desc: "Detailed exterior treatments around electrical conduits, plumbing entries, and utility lines."
}, {
  title: "Vent and eave treatment",
  desc: "Careful exterior applications around roofline overhangs, exterior vents, and upper siding seams."
}, {
  title: "Minor exclusion of small cracks and holes",
  desc: "Sealing minor exterior gaps and small openings to help reduce pest access routes into your home."
}];
export default function EntryPointTreatments() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="entry-point-treatments" id="entry-point-treatments" className="bg-background text-foreground py-20 md:py-28 border-b border-border scroll-mt-24">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mb-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="block h-px w-8 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-primary">
              Exterior Treatment
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Entry Point Treatments
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
            We apply targeted treatments around doors, windows, garage doors, utility and pipe penetrations, vents, eaves, and other exterior entry points, and we seal small cracks and small holes, to help keep pests from getting inside your home.
          </p>
        </div>

        <div ref={listRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {treatments.map((treatment, i) => <div key={i} data-index={i} className="border border-border bg-card rounded-md p-6 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="size-5 text-primary shrink-0" />
                  <h3 className="text-lg font-bold font-default text-foreground">
                    {treatment.title}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                  {treatment.desc}
                </p>
              </div>
            </div>)}

          <div className="border border-primary/30 bg-primary/5 rounded-md p-6 flex flex-col justify-center items-start">
            <Flame className="size-8 text-primary mb-3" />
            <h3 className="text-lg font-bold font-default text-foreground mb-2">
              Targeted Exterior Application
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our exclusion work is limited to small cracks and holes. We do not perform major repairs; if we find larger damage, we will let you know so you can have it repaired.
            </p>
          </div>
        </div>

        <div>
          <Button asChild className="rounded-md h-auto px-7 py-3.5 text-base font-bold font-default bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md">
            <Link to="/ContactPage">
              Get Your Free Inspection
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}